import { rebuildReactTooltip } from '@bpm-web-app/utils';
import { FC, useContext, useEffect, useRef } from 'react';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';

import { NavContext } from '../nav.context';
import styles from './nav-toggle-button.module.css';

interface Props {
    onCloseNav?: () => void;
}

export const NavToggleButton: FC<Props> = ({ onCloseNav }) => {
    const { isNavOpen, openNav, closeNav, isNavHidden } = useContext(NavContext);
    const pillRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        document.body.classList[!isNavOpen ? 'add' : 'remove']('nav-closed');
        document.body.classList[isNavHidden ? 'add' : 'remove']('nav-hidden');
        rebuildReactTooltip();
    }, [isNavOpen, isNavHidden]);

    return (
        <>
            <div className={styles['nav-edge-border']} />
            {/* Visual pill — moves on hover via CSS body class, no pointer events */}
            <div
                aria-hidden="true"
                className={classNames(styles['nav-edge-toggle'], {
                    [styles['nav-edge-toggle--closed']]: !isNavOpen,
                })}
            >
                <span ref={pillRef} className={styles['nav-edge-toggle__pill']} />
            </div>
            {/* Static hit zone — never moves, owns all events so hover state stays stable */}
            <button
                type="button"
                aria-label={isNavOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                data-for="nav-toggle-tip"
                data-tip={isNavOpen ? 'Collapse' : 'Expand'}
                onMouseEnter={() => document.body.classList.add('nav-toggle-hover')}
                onMouseLeave={() => document.body.classList.remove('nav-toggle-hover')}
                onClick={() => {
                    if (isNavOpen) {
                        closeNav();
                        if (typeof onCloseNav === 'function') {
                            onCloseNav();
                        }
                    } else {
                        openNav();
                    }
                }}
                className={styles['nav-edge-hit-zone']}
            />
            {/* Scoped tooltip — overridePosition reads the pill's live rect so it's always 8px from the pill regardless of hover movement */}
            <ReactTooltip
                id="nav-toggle-tip"
                place="right"
                effect="solid"
                delayShow={400}
                overridePosition={({ left, top }, _event, _target, node) => {
                    if (pillRef.current && node) {
                        const rect = pillRef.current.getBoundingClientRect();
                        return {
                            left: rect.right + 8,
                            top: rect.top + rect.height / 2 - node.clientHeight / 2,
                        };
                    }
                    return { left, top };
                }}
            />
        </>
    );
};
